import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, PieChart, Gauge, ShieldCheck, BarChart3, Rocket } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useCapTableSummary } from '@/hooks/useCapTableSummary';
import { NextBestActionsCard } from './NextBestActionsCard';
import { FounderProgressChecklist } from './FounderProgressChecklist';
import { DashboardTrustPanel } from './DashboardTrustPanel';

interface FirstRunDashboardProps {
  companyId: string;
  companyName: string;
  onDismiss: () => void;
}

export function FirstRunDashboard({ companyId, companyName, onDismiss }: FirstRunDashboardProps) {
  const navigate = useNavigate();
  const { summary, totalShares } = useCapTableSummary(companyId);
  const [founderOwnership, setFounderOwnership] = useState<number | null>(null);
  const [complianceStatus, setComplianceStatus] = useState<string>('Pending');
  const [valuation, setValuation] = useState<{ low: number; mid: number; high: number } | null>(
    null
  );

  useEffect(() => {
    if (summary.length > 0 && totalShares > 0) {
      const founderShares = summary.slice(0, 2).reduce((s, e) => s + (e.total_shares || 0), 0);
      setFounderOwnership(Math.round((founderShares / totalShares) * 100));
    }
  }, [summary, totalShares]);

  useEffect(() => {
    async function loadValuation() {
      // Try valuations table first
      const { data } = await supabase
        .from('valuations')
        .select('fmv_per_share')
        .eq('company_id', companyId)
        .order('as_of_date', { ascending: false })
        .limit(1);

      if (data && data.length > 0) {
        const fmv = data[0].fmv_per_share;
        const shares = totalShares || 1_000_000;
        const mid = fmv * shares;
        setValuation({ low: mid * 0.7, mid, high: mid * 1.4 });
        return;
      }

      // Fallback: onboarding valuation from audit_log
      const { data: logs } = await supabase
        .from('audit_log')
        .select('details')
        .eq('company_id', companyId)
        .eq('action', 'ONBOARDING_VALUATION')
        .order('created_at', { ascending: false })
        .limit(1);

      if (logs && logs.length > 0) {
        const d = logs[0].details as any;
        if (d?.low && d?.high) {
          setValuation({ low: d.low, mid: (d.low + d.high) / 2, high: d.high });
        }
      }
    }

    async function checkCompliance() {
      const { count } = await supabase
        .from('compliance_checks')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .eq('passed', false);
      setComplianceStatus((count || 0) > 0 ? `${count} issues` : 'All clear');
    }

    loadValuation();
    checkCompliance();
  }, [companyId, totalShares]);

  const fmt = (n: number) =>
    n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : `$${(n / 1_000).toFixed(0)}K`;

  const kpis = [
    {
      label: 'Founder Ownership',
      value: founderOwnership !== null ? `${founderOwnership}%` : '—',
      sub:
        founderOwnership !== null
          ? `of ${totalShares.toLocaleString()} shares`
          : 'Add shareholders to track',
      icon: PieChart,
    },
    {
      label: 'Runway',
      value: '—',
      sub: 'Add financials to calculate',
      icon: Gauge,
    },
    {
      label: 'Compliance',
      value: complianceStatus,
      sub: complianceStatus === 'All clear' ? 'No issues detected' : 'Review recommended',
      icon: ShieldCheck,
    },
  ];

  const quickLinks = [
    { label: 'Valuation Advisor', icon: BarChart3, path: '/funding/valuation' },
    { label: 'Cap Table', icon: PieChart, path: '/companies' },
    { label: 'Set Up Round', icon: Rocket, path: '/funding/round-setup' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Welcome to CapLedger</h1>
          <p className="text-muted-foreground mt-1">
            Here's where <span className="font-medium text-foreground">{companyName}</span> stands
            right now.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="text-xs text-muted-foreground"
        >
          Switch to full dashboard
        </Button>
      </div>

      {/* Dominant Valuation Card + KPI Strip */}
      <div className="grid gap-4 md:grid-cols-4">
        {/* Valuation — spans 1 column but taller / more prominent */}
        <Card className="border-primary/20 bg-primary/[0.03] md:row-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <p className="text-xs font-medium uppercase tracking-wider text-primary/70">
              Estimated Valuation
            </p>
            <TrendingUp className="h-4 w-4 text-primary/50" />
          </CardHeader>
          <CardContent>
            {valuation ? (
              <div>
                <div className="text-3xl font-bold tabular-nums text-primary">
                  {fmt(valuation.mid)}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Range: {fmt(valuation.low)} – {fmt(valuation.high)}
                </p>
              </div>
            ) : (
              <div>
                <div className="text-2xl font-semibold tabular-nums">—</div>
                <p className="text-xs text-muted-foreground mt-1">
                  <button
                    onClick={() => navigate('/funding/valuation')}
                    className="text-primary hover:underline"
                  >
                    Complete valuation →
                  </button>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Other KPIs */}
        {kpis.map((kpi) => (
          <Card key={kpi.label} variant="stat">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {kpi.label}
              </p>
              <kpi.icon className="h-4 w-4 text-muted-foreground/60" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">{kpi.value}</div>
              <p className="text-xs text-muted-foreground mt-1">{kpi.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Middle: Actions + Progress */}
      <div className="grid gap-4 lg:grid-cols-2">
        <NextBestActionsCard companyId={companyId} />
        <FounderProgressChecklist companyId={companyId} />
      </div>

      {/* Lower: Quick Links + Trust */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <h3 className="text-lg font-medium">Go deeper</h3>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-3">
                {quickLinks.map((link) => (
                  <button
                    key={link.label}
                    onClick={() => navigate(link.path)}
                    className="flex flex-col items-center gap-2 p-6 rounded-lg border border-border hover:border-primary/30 hover:bg-muted/30 transition-all group"
                  >
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                      <link.icon className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-sm font-medium">{link.label}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <DashboardTrustPanel companyId={companyId} />
      </div>
    </div>
  );
}
